// UI section for per-country version and release-note differences.
// Rendered below the main comparison table.

import { groupByVersion, findUniqueReleaseNotes, findVersionOutliers } from '../domain/versionNotes.js';
import { t, countryName } from '../i18n/index.js';

const SNIPPET_MAX = 300;

/**
 * Render the "Version Notes" section into `container`.
 *
 * If no rows have version data the container is cleared and hidden; otherwise
 * the container gets a collapsible <details> (collapsed by default, natively
 * keyboard-navigable) containing:
 *   - a version uniformity notice (simple case), or
 *   - a "staggered rollout" highlight plus a per-version list with country
 *     chips when storefronts diverge, and
 *   - the release-note differences across storefronts (only when present).
 *
 * @param {import('../domain/normalizeApp.js').RegionRow[]} rows
 * @param {HTMLElement|null} container
 */
export function renderVersionNotes(rows, container) {
  if (!container) return;

  const { allSame, groups } = groupByVersion(rows);
  const { mostCommonNotes, uniqueEntries } = findUniqueReleaseNotes(rows);
  const { staggered, leadVersion, outliers } = findVersionOutliers(rows);

  const hasVersionData = groups.length > 0;
  if (!hasVersionData) {
    container.hidden = true;
    container.innerHTML = '';
    return;
  }

  container.hidden = false;

  // Wrap the whole section in a <details> so it is collapsible. It starts
  // collapsed (no `open`) to keep simple comparisons clutter-free; the summary
  // carries a "staggered rollout" badge so the signal is visible while closed.
  const details = document.createElement('details');
  details.className = 'version-notes-details';

  const topSummary = document.createElement('summary');
  topSummary.className = 'version-notes-summary';
  topSummary.append(t('versionNotes'));
  if (staggered) {
    const badge = document.createElement('span');
    badge.className = 'version-notes-badge';
    badge.textContent = t('staggeredRollout');
    topSummary.append(badge);
  }
  details.appendChild(topSummary);

  const section = document.createElement('section');
  section.className = 'version-notes-section';
  section.setAttribute('aria-label', t('versionNotes'));

  if (allSame) {
    // Simple case: one version everywhere — a concise line, no chip clutter.
    const p = document.createElement('p');
    p.className = 'version-same';
    p.textContent = t('allSameVersion', { version: groups[0].version });
    section.appendChild(p);
  } else {
    // Staggered rollout: highlight the lead version and how many lag, then list
    // every live version with the storefronts running it.
    const outlierCount = outliers.reduce((n, g) => n + g.countries.length, 0);

    const banner = document.createElement('p');
    banner.className = 'version-staggered';
    const label = document.createElement('strong');
    label.className = 'version-staggered__label';
    label.textContent = t('staggeredRollout');
    banner.appendChild(label);
    banner.append(
      ` — ${t('staggeredRolloutDetail', { version: leadVersion, count: outlierCount })}`,
    );
    section.appendChild(banner);

    const diffHeading = document.createElement('p');
    diffHeading.className = 'version-diff-label';
    diffHeading.textContent = t('versionDifferences');
    section.appendChild(diffHeading);

    const versionList = document.createElement('ul');
    versionList.className = 'version-group-list';
    for (const group of groups) {
      const li = document.createElement('li');
      li.className = 'version-group';
      if (group.version !== leadVersion) li.classList.add('version-group--outlier');

      const versionLabel = document.createElement('span');
      versionLabel.className = 'version-group__version';
      versionLabel.textContent = `v${group.version}`;
      li.appendChild(versionLabel);

      li.appendChild(buildChipRow(group.countries));
      versionList.appendChild(li);
    }
    section.appendChild(versionList);
  }

  // Release-note differences (only when storefronts actually diverge).
  if (uniqueEntries.length > 0) {
    const releaseDetails = document.createElement('details');
    releaseDetails.className = 'release-notes-details';

    const summary = document.createElement('summary');
    summary.textContent = t('releaseNotesDiff');
    releaseDetails.appendChild(summary);

    if (mostCommonNotes) {
      const commonHeader = document.createElement('p');
      commonHeader.className = 'release-notes-common-header';
      commonHeader.textContent = t('releaseNotesCommon', { notes: snippet(mostCommonNotes) });
      releaseDetails.appendChild(commonHeader);
    }

    const entriesList = document.createElement('ul');
    entriesList.className = 'release-notes-entries';

    for (const entry of uniqueEntries) {
      const li = document.createElement('li');
      li.className = 'release-note-entry';

      const head = document.createElement('div');
      head.className = 'release-note-entry__head';
      head.appendChild(countryChip(entry.countryCode, entry.countryName));
      const name = document.createElement('span');
      name.className = 'release-note-entry__country';
      name.textContent = countryName(entry.countryCode, entry.countryName);
      head.appendChild(name);
      if (entry.version) {
        const ver = document.createElement('span');
        ver.className = 'release-note-entry__version';
        ver.textContent = `v${entry.version}`;
        head.appendChild(ver);
      }
      li.appendChild(head);

      const notes = document.createElement('p');
      notes.className = 'release-note-entry__notes';
      notes.textContent = snippet(entry.releaseNotes);
      li.appendChild(notes);

      entriesList.appendChild(li);
    }

    releaseDetails.appendChild(entriesList);
    section.appendChild(releaseDetails);
  }

  details.appendChild(section);
  container.innerHTML = '';
  container.appendChild(details);
}

/**
 * Build a row of country-code chips.
 * @param {string[]} codes
 * @returns {HTMLUListElement}
 */
function buildChipRow(codes) {
  const row = document.createElement('ul');
  row.className = 'version-group__chips';
  for (const code of codes) {
    const item = document.createElement('li');
    item.appendChild(countryChip(code));
    row.appendChild(item);
  }
  return row;
}

/**
 * A single country-code chip whose accessible name is the localized country
 * name (so screen readers announce "Japan", not "J P").
 * @param {string} code
 * @param {string} [fallbackName]
 * @returns {HTMLSpanElement}
 */
function countryChip(code, fallbackName) {
  const chip = document.createElement('span');
  chip.className = 'cc';
  chip.textContent = String(code).toUpperCase();
  const name = countryName(code, fallbackName ?? String(code).toUpperCase());
  chip.title = name;
  chip.setAttribute('aria-label', name);
  return chip;
}

/**
 * Truncate long release-note text for display.
 * @param {string} text
 * @returns {string}
 */
function snippet(text) {
  return text.length > SNIPPET_MAX ? `${text.slice(0, SNIPPET_MAX)}…` : text;
}
